import { useState } from 'react';
import { SharedStream } from '@/lib/shared-types';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { UserHoverCard } from '@/components/ui/user-hover-card';
import { Separator } from '@/components/ui/separator';
import { StreamShareDialog } from './stream-share-dialog';
import {
  Clock,
  Calendar,
  Eye,
  Share,
  Tag,
  Info,
  ExternalLink
} from 'lucide-react';
import { Link } from 'react-router-dom';

interface StreamDetailsSheetProps {
  stream: SharedStream;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function StreamDetailsSheet({ stream, open, onOpenChange }: StreamDetailsSheetProps) {
  const [isLiked, setIsLiked] = useState(false);

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'live':
        return <Badge variant="destructive" className="animate-pulse">LIVE</Badge>;
      case 'scheduled':
        return <Badge variant="secondary">Scheduled</Badge>;
      case 'ended':
        return <Badge variant="outline">Ended</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const formatScheduledTime = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = date.getTime() - now.getTime();
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffHours / 24);

    if (diffDays > 0) {
      return `${diffDays} day${diffDays > 1 ? 's' : ''} away`;
    } else if (diffHours > 0) {
      return `In ${diffHours} hour${diffHours > 1 ? 's' : ''}`;
    } else {
      return 'Starting soon';
    }
  };

  const formatDateTime = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-lg overflow-y-auto">
        <SheetHeader>
          <div className="flex items-start gap-3 mb-4">
            <div className="flex-1">
              <SheetTitle className="text-xl leading-tight">{stream.title}</SheetTitle>
              <div className="flex items-center gap-2 mt-2">
                {getStatusBadge(stream.status)}
                {stream.status === 'live' && stream.viewerCount !== undefined && (
                  <div className="flex items-center gap-1 text-sm text-muted-foreground">
                    <Eye className="h-3 w-3" />
                    {stream.viewerCount} watching
                  </div>
                )}
              </div>
            </div>
          </div>
        </SheetHeader>

        <div className="space-y-6">
          {/* Creator Info */}
          <div className="flex items-center gap-3">
            <UserHoverCard user={{ name: stream.creator?.name || `Creator #${stream.createdBy}`, profilePictureUrl: stream.creator?.profilePictureUrl }}>
              <Avatar className="h-10 w-10">
                <AvatarImage src={stream.creator?.profilePictureUrl} />
                <AvatarFallback>
                  {stream.creator?.name?.charAt(0)?.toUpperCase() || 'U'}
                </AvatarFallback>
              </Avatar>
            </UserHoverCard>
            <div>
              <p className="font-medium">{stream.creator?.name || `Creator #${stream.createdBy}`}</p>
              <p className="text-sm text-muted-foreground">Stream Creator</p>
            </div>
          </div>

          {/* Description */}
          {stream.description && (
            <div>
              <h4 className="font-medium mb-2 flex items-center gap-2">
                <Info className="h-4 w-4" />
                Description
              </h4>
              <p className="text-sm text-muted-foreground leading-relaxed">
                {stream.description}
              </p>
            </div>
          )}

          {/* Schedule/Time Info */}
          <div className="space-y-3">
            <h4 className="font-medium flex items-center gap-2">
              <Calendar className="h-4 w-4" />
              Schedule & Details
            </h4>

            <div className="grid grid-cols-1 gap-3 text-sm">
              {stream.scheduledStart && (
                <div className="flex items-center gap-2">
                  <Clock className="h-4 w-4 text-muted-foreground" />
                  <div>
                    <p className="font-medium">
                      {stream.status === 'scheduled' ? 'Scheduled for' : 'Started at'}
                    </p>
                    <p className="text-muted-foreground">
                      {formatDateTime(stream.scheduledStart)}
                    </p>
                    {stream.status === 'scheduled' && (
                      <p className="text-xs text-muted-foreground mt-1">
                        {formatScheduledTime(stream.scheduledStart)}
                      </p>
                    )}
                  </div>
                </div>
              )}

              {stream.duration && (
                <div className="flex items-center gap-2">
                  <Clock className="h-4 w-4 text-muted-foreground" />
                  <div>
                    <p className="font-medium">Duration</p>
                    <p className="text-muted-foreground">
                      {Math.floor(stream.duration / 60)}m {stream.duration % 60}s
                    </p>
                  </div>
                </div>
              )}

              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4 text-muted-foreground" />
                <div>
                  <p className="font-medium">Created</p>
                  <p className="text-muted-foreground">
                    {new Date(stream.createdAt).toLocaleDateString()}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Tags */}
          {(() => {
            const tags = Array.isArray(stream.tags) ? stream.tags : [];
            return tags.length > 0 && (
              <div>
                <h4 className="font-medium mb-2 flex items-center gap-2">
                  <Tag className="h-4 w-4" />
                  Tags
                </h4>
                <div className="flex flex-wrap gap-2">
                  {tags.map((tag: string) => (
                    <Badge key={tag} variant="secondary" className="text-xs">
                      {tag}
                    </Badge>
                  ))}
                </div>
              </div>
            );
          })()}

          {/* Access Control Info */}
          <div className="bg-muted/50 rounded-lg p-4">
            <h4 className="font-medium mb-2">Access Information</h4>
            <div className="text-sm text-muted-foreground">
              {stream.has_access === false ? (
                <div className="space-y-2">
                  <p>This stream requires special access.</p>
                  <div className="flex gap-2">
                    <Button size="sm" asChild>
                      <Link to="/auth/signup">Sign Up</Link>
                    </Button>
                    <Button size="sm" variant="outline" asChild>
                      <Link to="/streams">Browse Other Streams</Link>
                    </Button>
                  </div>
                </div>
              ) : (
                <p className="text-green-600">✓ You have access to this stream</p>
              )}
            </div>
          </div>

          <Separator />

          {/* Action Buttons */}
          <div className="space-y-3">
            <div className="flex gap-2">
              <Button
                variant={isLiked ? "default" : "outline"}
                size="sm"
                onClick={() => setIsLiked(!isLiked)}
                className="flex-1"
              >
                {isLiked ? 'Liked' : 'Like'}
              </Button>

              <StreamShareDialog
                streamId={stream.id.toString()}
                streamTitle={stream.title}
              >
                <Button variant="outline" size="sm" className="flex-1">
                  <Share className="h-4 w-4 mr-2" />
                  Share
                </Button>
              </StreamShareDialog>
            </div>

            {stream.has_access !== false && (
              <Button asChild className="w-full">
                <Link to={`/streams/${stream.id}`}>
                  <ExternalLink className="h-4 w-4 mr-2" />
                  {stream.status === 'live' ? 'Watch Live' : 'View Full Stream'}
                </Link>
              </Button>
            )}
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
